import { lazy, Suspense, type ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { loadAudienceLayerModule } from "@/lib/audienceLayerRemote";
import type { AudienceLayerHostContext } from "../Pages/Watch/VideoPlayerLive/useAudienceLayerHost";
import "./AudienceLayerPanel.css";

/*
 * Loaded at runtime from the separate Audience Layer deploy, and NOT declared
 * in the build config: see lib/audienceLayerRemote for what declaring it there
 * cost every page of this app. A load or runtime failure here is contained
 * below and never reaches the video player.
 */
const AudienceLayer = lazy(() =>
  loadAudienceLayerModule<{
    default: React.ComponentType<{ hostContext?: AudienceLayerHostContext }>;
  }>("App"),
);

type Props = {
  // Null when nobody is signed in: there is no fan to attribute messages or
  // reactions to, and every request the panel made would 401.
  hostContext?: AudienceLayerHostContext | null;
  // What to show instead, which on the Watch page is the sidebar this panel
  // replaces. Rendered whenever the layer cannot be: signed out, or the remote
  // failed to load. Losing the chat entirely because a separately deployed
  // bundle is unreachable would be a worse outage than the one it is meant to
  // contain.
  fallback?: ReactNode;
  /**
   * What to show over the panel while a phone owns the Audience Layer for
   * this game (the second-screen pairing card).
   *
   * The panel stays mounted and merely goes inert, rather than being
   * replaced: the remote underneath is the real tabs, and disconnecting
   * restores them instantly with chat history and scroll position intact
   * instead of refetching. Presence is already per-person rather than
   * per-tab on the API side, so a desktop and a phone connected at once
   * still count as one fan.
   */
  blocked?: ReactNode;
};

export function AudienceLayerPanel({ hostContext, fallback = null, blocked }: Props) {
  // Signed out: there is no fan to attribute anything to, so the remote is
  // neither rendered nor fetched.
  if (!hostContext) return <>{fallback}</>;

  return (
    // The <aside> lives INSIDE the ErrorBoundary so a remote failure replaces
    // the whole slot rather than leaving an empty 380px column beside the
    // video. The Suspense skeleton reserves the slot only while loading.
    <ErrorBoundary
      fallbackRender={() => <>{fallback}</>}
      onError={(err) => console.error("[AL] runtime error:", err)}
    >
      {/*
        `display: contents`, and it MUST stay that way.

        This wrapper exists only to hold the blocked overlay beside the panel.
        A plain <div> here generates a box, and above 1080px that box becomes
        the panel's containing block, because AudienceLayerPanel.css positions
        the panel absolutely at that width and alSidebarGridProps sets
        `position: relative` on the grid item precisely to be that containing
        block. Interposing a box takes the role from the grid item, and since
        the panel is then out of the wrapper's flow the wrapper has no height
        to give it: the tab row keeps its intrinsic height and everything below
        collapses to nothing. That is what a plain div here actually looked
        like, and no test catches it.

        A `display: contents` box is not generated at all, so the grid item
        stays the containing block for both children.
      */}
      <div style={{ display: "contents" }}>
        <aside
          className="audience-layer-panel"
          // React 18 does not know the `inert` attribute and warns on a bare
          // prop, so it is spread. React 19 supports it directly, and this
          // can become a plain attribute when this app upgrades.
          {...(blocked ? { inert: "" } : {})}
          style={blocked ? { filter: "grayscale(1)", opacity: 0.45 } : undefined}
        >
          <Suspense fallback={<AudienceLayerSkeleton />}>
            <AudienceLayer hostContext={hostContext} />
          </Suspense>
        </aside>
        {blocked && (
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
            {blocked}
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

function AudienceLayerSkeleton() {
  return <div className="audience-layer-skeleton" />;
}
